'use client'

import { useCallback, useEffect, useRef } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { cn } from '@/lib/utils'

import type { ChatMessageListProps } from './types'
import { ChatMessage } from './ChatMessage'

/** Walk up the DOM to find the nearest scrollable ancestor. */
function getScrollParent(el: HTMLElement | null): HTMLElement | null {
  let node = el?.parentElement ?? null
  while (node) {
    const { overflowY } = getComputedStyle(node)
    if (overflowY === 'auto' || overflowY === 'scroll') return node
    node = node.parentElement
  }
  return null
}

/**
 * Scrollable container for chat messages with auto-scroll on new
 * messages. Used by both CopilotChat (side-panel) and AnalyticsChat
 * (full-page). The parent controls width constraints; this component
 * handles scroll behavior and message iteration.
 *
 * Auto-scroll only fires when the user is already near the bottom of
 * the scroll container, so reviewing earlier messages is not disrupted.
 */
export function ChatMessageList({
  messages,
  thinkingMessageId,
  toolCallCount,
  currentToolCalls,
  onCopyMessage,
  copiedMessageId,
  className,
  isStreaming,
  onRespondToElement,
  onSendMessage,
}: ChatMessageListProps) {
  const bottomRef = useRef<HTMLDivElement>(null)
  const isNearBottom = useRef(true)
  const scrollParentRef = useRef<HTMLElement | null>(null)

  // Resolve and cache the scroll parent once the ref is populated.
  const resolveScrollParent = useCallback(() => {
    if (!scrollParentRef.current && bottomRef.current) {
      scrollParentRef.current = getScrollParent(bottomRef.current)
    }
    return scrollParentRef.current
  }, [])

  // Track whether the user is near the bottom of the scroll container.
  useEffect(() => {
    const container = resolveScrollParent()
    if (!container) return

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container
      isNearBottom.current = scrollHeight - scrollTop - clientHeight < 150
    }

    container.addEventListener('scroll', handleScroll, { passive: true })
    return () => container.removeEventListener('scroll', handleScroll)
  }, [resolveScrollParent])

  // Auto-scroll to the bottom when messages change, but only if the
  // user has not scrolled up to review earlier content.
  useEffect(() => {
    if (!isNearBottom.current) return

    const container = resolveScrollParent()
    if (container) {
      // Use requestAnimationFrame so the DOM has finished layout after
      // new content (especially large visualizations) has been inserted.
      requestAnimationFrame(() => {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: 'smooth',
        })
      })
    }
  }, [messages, resolveScrollParent])

  return (
    <div className={cn('flex flex-col gap-0', className)}>
      <AnimatePresence initial={false}>
        {messages.map(message => (
          <motion.div
            key={message.id}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.15 }}
          >
            <ChatMessage
              message={message}
              isThinking={thinkingMessageId === message.id}
              toolCallCount={thinkingMessageId === message.id ? toolCallCount : undefined}
              currentToolCalls={thinkingMessageId === message.id ? currentToolCalls : undefined}
              onCopy={onCopyMessage}
              isCopied={copiedMessageId === message.id}
              isStreaming={isStreaming && message.status === 'streaming'}
              onRespondToElement={onRespondToElement}
              onSendMessage={onSendMessage}
            />
          </motion.div>
        ))}
      </AnimatePresence>
      <div ref={bottomRef} />
    </div>
  )
}
